import { useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Home, Smartphone, Shield, CreditCard, Heart, Film,
  FileText, ShoppingCart, Phone, ChevronRight, Check, Star,
  Clock, MapPin, UserCheck, Users, Briefcase
} from 'lucide-react';
import SEO from '../components/SEO';
import DigiMitraBookingForm from '../components/DigiMitraBookingForm';
import DigiMitraApplyWizard from '../components/DigiMitraApplyWizard';
import SlotsTodayBadge from '../components/SlotsTodayBadge';

const DigiMitra = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [showApply, setShowApply] = useState(false);

  const services = [
    {
      icon: Smartphone,
      title: 'Smartphone Setup & Basics',
      color: '#2563eb',
      items: [
        'Setting up a new smartphone from scratch',
        'Transferring contacts, photos, and data from an old phone',
        'Adjusting display size, font, brightness, and sound for comfort',
        'Setting up Wi-Fi, mobile data, and Bluetooth',
        'Teaching basic gestures — swipe, tap, scroll, zoom',
      ],
    },
    {
      icon: Phone,
      title: 'Video Calls & Staying Connected',
      color: '#16a34a',
      items: [
        'Setting up WhatsApp and making video calls to family',
        'Using Google Duo / FaceTime for high-quality calls',
        'Joining WhatsApp groups and sending voice messages',
        'Sharing photos and videos with grandchildren',
        'Setting up and using email (Gmail, Yahoo)',
      ],
    },
    {
      icon: CreditCard,
      title: 'Digital Payments & Banking',
      color: '#7c3aed',
      items: [
        'Setting up UPI (Google Pay, PhonePe, Paytm)',
        'Making safe digital payments and transfers',
        'Checking bank balance and transaction history',
        'Paying electricity, water, gas, and mobile bills',
        'Understanding and avoiding payment scams',
      ],
    },
    {
      icon: Shield,
      title: 'Online Safety & Fraud Prevention',
      color: '#dc2626',
      items: [
        'Identifying fake calls, messages, and WhatsApp forwards',
        'Recognizing phishing links and OTP scams',
        'Setting strong passwords and enabling 2-factor authentication',
        'Privacy settings for WhatsApp, Facebook, and other apps',
        'What to do if you suspect you\'ve been scammed',
      ],
    },
    {
      icon: ShoppingCart,
      title: 'Online Shopping & Food Delivery',
      color: '#ea580c',
      items: [
        'Ordering groceries on BigBasket, Blinkit, or Zepto',
        'Ordering food on Swiggy or Zomato',
        'Shopping on Amazon and Flipkart safely',
        'Tracking orders and managing returns',
        'Comparing prices and reading reviews',
      ],
    },
    {
      icon: Heart,
      title: 'Health & Telemedicine',
      color: '#e5266a',
      items: [
        'Booking doctor appointments on Practo or Apollo',
        'Ordering medicines online (PharmEasy, 1mg, Tata Health)',
        'Setting up medication reminders and health trackers',
        'Accessing lab reports and health records digitally',
        'Using emergency SOS features on your phone',
      ],
    },
    {
      icon: FileText,
      title: 'Government Services & Documents',
      color: '#0891b2',
      items: [
        'Checking and applying for pension schemes',
        'Aadhaar updates, PAN card, and Voter ID services',
        'Passport renewal and tracking applications',
        'Filing income tax returns (ITR) online',
        'Accessing DigiLocker for digital documents',
      ],
    },
    {
      icon: Film,
      title: 'Entertainment & Learning',
      color: '#a855f7',
      items: [
        'Setting up YouTube and discovering content you love',
        'Using Spotify or Gaana for music and podcasts',
        'Watching movies on Netflix, Hotstar, or JioCinema',
        'Reading news and e-books on your device',
        'Starting your own YouTube channel (Elderra YouTuber Program)',
      ],
    },
    {
      icon: Home,
      title: 'Concierge & Daily Tasks',
      color: '#059669',
      items: [
        'Booking cabs (Ola, Uber) and train tickets (IRCTC)',
        'Booking flight tickets and hotels for travel',
        'Filling online forms for government or private services',
        'Setting up calendar reminders and alarms',
        'Help with any other digital task you need assistance with',
      ],
    },
  ];

  const steps = [
    { num: '1', icon: Phone, title: 'Book a Visit', desc: 'Fill a simple form or call us. Pick a date and time that suits you.' },
    { num: '2', icon: UserCheck, title: 'DigiMitra Arrives', desc: 'A trained, verified DigiMitra service provider visits your home.' },
    { num: '3', icon: Star, title: 'Get Things Done', desc: 'Learn at your pace. Ask any question. No tech jargon — just simple guidance.' },
  ];

  return (
    <div className="dm-page" data-testid="digimitra-page">
      <SEO
        title="DigiMitra Home Visits | Elderra - Tech Help at Your Doorstep"
        description="Book a DigiMitra home visit for personalized tech help. Smartphone setup, digital payments, online safety, video calls, government services, and more. ₹499 per hour."
        keywords="DigiMitra, senior tech support, home visit tech help, digital training elderly, smartphone help seniors India"
      />

      {/* Hero */}
      <section className="dm-page-hero">
        <div className="container">
          <div className="dm-page-hero-inner">
            <div className="dm-page-hero-text">
              <div className="dm-page-badge">Trained Service Providers</div>
              <h1 data-testid="dm-page-title">DigiMitra Home Visits</h1>
              <p className="dm-page-hero-sub">
                Your friendly, trained tech helper who comes to your home and patiently teaches you 
                everything — from making video calls to paying bills online.
              </p>
              <div className="dm-page-hero-highlights">
                <div className="dm-page-hl"><Clock size={18} /> 1-hour visit</div>
                <div className="dm-page-hl"><MapPin size={18} /> At your home</div>
                <div className="dm-page-hl"><UserCheck size={18} /> Verified & trained</div>
              </div>
              <div className="dm-page-hero-actions">
                <SlotsTodayBadge serviceType="digimitra" accent="dm" testId="dm-slots-today" />
                <Button className="dm-page-cta" onClick={() => setShowBooking(true)} data-testid="dm-page-book-btn">
                  Book a Visit — &#8377;499/hr <ChevronRight size={20} />
                </Button>
              </div>
            </div>
            <div className="dm-page-hero-img">
              <img
                src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/wb5kt71f_Bilal%20Coaching.jpeg"
                alt="DigiMitra helping a senior"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="dm-page-how">
        <div className="container">
          <h2 data-testid="dm-how-title">How It Works</h2>
          <div className="dm-page-steps">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="dm-page-step" data-testid={`dm-step-${i + 1}`}>
                  <div className="dm-page-step-num">{s.num}</div>
                  <div className="dm-page-step-icon"><Icon size={28} /></div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Services */}
      <section className="dm-page-services">
        <div className="container">
          <h2 data-testid="dm-services-title">Everything a DigiMitra Can Help With</h2>
          <p className="dm-page-services-sub">
            Our trained service providers can help with any digital task. Here's a detailed look at what we cover.
          </p>
          <div className="dm-page-svc-grid">
            {services.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <div key={i} className="dm-page-svc-card" data-testid={`dm-svc-card-${i}`}>
                  <div className="dm-page-svc-header">
                    <div className="dm-page-svc-icon" style={{ color: svc.color, background: `${svc.color}12` }}>
                      <Icon size={24} />
                    </div>
                    <h3>{svc.title}</h3>
                  </div>
                  <ul className="dm-page-svc-list">
                    {svc.items.map((item, j) => (
                      <li key={j}><Check size={14} style={{ color: svc.color }} /> {item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="dm-page-cta-banner">
        <div className="container">
          <div className="dm-page-cta-inner">
            <h2>Ready to Get Started?</h2>
            <p>Book a DigiMitra visit today. No tech jargon. Just patient, friendly guidance at your home.</p>
            <Button className="dm-page-cta" onClick={() => setShowBooking(true)} data-testid="dm-page-bottom-book-btn">
              Book a Home Visit — &#8377;499/hr <ChevronRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Become a DigiMitra */}
      <section className="dm-apply-section" data-testid="dm-apply-section">
        <div className="container">
          <div className="dm-apply-card">
            <div className="dm-apply-content">
              <div className="dm-apply-badge"><Briefcase size={14} /> Career Opportunity</div>
              <h2>Become a DigiMitra Associate</h2>
              <p>
                Are you passionate about helping seniors navigate the digital world? Join our team of 
                trained service providers and make a real difference in the lives of India's elderly.
              </p>
              <div className="dm-apply-perks">
                <div className="dm-apply-perk"><Check size={16} /> Flexible working hours</div>
                <div className="dm-apply-perk"><Check size={16} /> Training provided</div>
                <div className="dm-apply-perk"><Check size={16} /> Meaningful work</div>
                <div className="dm-apply-perk"><Check size={16} /> Competitive compensation</div>
              </div>
              <Button className="dm-apply-btn" onClick={() => setShowApply(true)} data-testid="dm-apply-btn">
                Apply Now <ChevronRight size={18} />
              </Button>
            </div>
            <div className="dm-apply-visual">
              <Users size={80} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}

      <DigiMitraApplyWizard open={showApply} onOpenChange={setShowApply} />
      <DigiMitraBookingForm open={showBooking} onOpenChange={setShowBooking} />
    </div>
  );
};

export default DigiMitra;
